import type { InternalAxiosRequestConfig } from "axios";
import { CELLS, findCell, isRemoteCell } from "./cells";
import { getCellSessionToken } from "./cell-session";
import { ConflictingTenantsError, resolveTenantCell } from "./tenant-cells";

declare module "axios" {
  interface AxiosRequestConfig {
    /**
     * The tenant a request acts on, for requests that don't carry it in the
     * `tenant_id` query param. Used only to pick the cell; never sent.
     */
    routeTenantId?: string;
    /**
     * The cell to send a request to, for requests that act on several
     * tenants of one cell rather than on a single tenant. Never sent.
     */
    routeCellId?: string;
  }
}

/**
 * The tenant a request acts on: the explicit hint, or `tenant_id` in the
 * query params or in the URL's own query string. A request naming two
 * different tenants is refused: the backend may act on one and the routing
 * on the other.
 */
function targetTenantId(
  config: InternalAxiosRequestConfig,
): string | undefined {
  const named = new Set<string>();
  if (config.routeTenantId) {
    named.add(config.routeTenantId);
  }
  const tenantParam: unknown = (
    config.params as { tenant_id?: unknown } | undefined
  )?.tenant_id;
  if (typeof tenantParam === "string" && tenantParam) {
    named.add(tenantParam);
  }
  if (config.url?.includes("tenant_id=")) {
    const query = new URL(config.url, "http://relative.invalid").searchParams;
    for (const value of query.getAll("tenant_id")) {
      if (value) {
        named.add(value);
      }
    }
  }
  if (named.size > 1) {
    throw new ConflictingTenantsError([...named]);
  }
  return named.values().next().value;
}

/**
 * Sends a tenant-scoped request to the cell that tenant lives in. Requests
 * through the backoffice API name their target tenant in a `tenant_id`
 * query param. For a tenant in another cell, this points the request at that
 * cell's API under `servicePath` and signs it with that cell's session, and
 * returns true. A `routeCellId` hint names the cell directly. Primary-cell
 * and untargeted requests are left as they are.
 */
export async function routeToTenantCell(
  config: InternalAxiosRequestConfig,
  servicePath: string,
): Promise<boolean> {
  if (CELLS.length === 1) {
    return false;
  }

  const tenantId = targetTenantId(config);
  let cell;
  if (config.routeCellId) {
    cell = findCell(config.routeCellId);
    if (!cell) {
      throw new Error(`Unknown cell ${config.routeCellId}; not sent.`);
    }
    if (tenantId && (await resolveTenantCell(tenantId)).id !== cell.id) {
      throw new Error(
        `Request names cell ${cell.id} and tenant ${tenantId}, which is in another cell; not sent.`,
      );
    }
  } else if (tenantId) {
    cell = await resolveTenantCell(tenantId);
  } else {
    return false;
  }
  if (!isRemoteCell(cell)) {
    return false;
  }

  config.baseURL = `${cell.remote.apiBaseUrl}${servicePath}`;
  config.headers.set(
    "Authorization",
    `Bearer ${await getCellSessionToken(cell)}`,
  );
  return true;
}
